import React, { useCallback, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import SideNavigation, {
  SideNavigationProps,
} from "@amzn/awsui-components-react/polaris/side-navigation";
import {
  DEFAULT_LEFT_NAV_PATH,
  LEFT_NAV_ITEMS,
  LeftNavPath,
  resolveLeftNavPath,
} from "./shellRoutes";

/**
 * Persistent left navigation panel of the App Shell (V2 Route).
 *
 * Renders a Cloudscape `SideNavigation` with:
 * - the Left Nav Header text `"Devices ASIN Central"` above the primary
 *   items, with `href` pointing at the default Left Nav path
 *   (`/v2/search`) so that clicking the header restores the initial view;
 * - the four primary Left Nav items in the order mandated -
 *   "ASIN Search", "ASIN Date Baseline", "Validation", "Launch" - each
 *   bound to its user-visible V2 Route sub-path;
 * - a Wiki Link entry rendered as an inert Cloudscape `link` item with
 *   `disabled: true`, satisfying R1.11 and R1.12 (visible-but-inert; no
 *   navigation state change, no URL path change, no Global Search State
 *   change on activation).
 *
 * The active item is derived from `useLocation().pathname` via
 * {@link resolveLeftNavPath}. Because Cloudscape marks whichever item's
 * `href` equals `activeHref` as the "active" item, and every primary
 * item has a distinct `href`, "never render two items as active
 * simultaneously" - is satisfied by construction.
 *
 * Path handling is defensive: the App Shell mounts this component inside
 * a `<BrowserRouter basename="/v2">`, so `useLocation().pathname` returns
 * a path WITHOUT the `/v2` prefix (e.g. `/search`, `/baseline`). Unit
 * tests, however, may render `<LeftNav/>` directly under a plain
 * `MemoryRouter` at paths that DO include `/v2`. Both forms are accepted
 * and normalised to a single {@link LeftNavPath} before matching.
 */

/**
 * User-visible prefix for the V2 Route. The `href` values on all Left Nav
 * items and on the Left Nav Header are browser-visible URLs and therefore
 * include this prefix, even though the `LeftNavPath` values exported by
 * `shellRoutes.ts` are relative to `basename="/v2"`.
 */
const V2_ROUTE_PREFIX = "/v2";

/**
 * Left Nav Header text mandated by R1.10.
 */
const HEADER_TEXT = "Devices ASIN Central";

/**
 * Text of the Wiki Link entry rendered beneath the four primary items
 * (R1.11). The Wiki Link's target destination is out of scope for V1;
 * the item exists solely as a visible-but-inert affordance.
 */
const WIKI_LINK_TEXT = "Wiki";

/**
 * `href` value assigned to the Wiki Link item. Cloudscape's
 * `SideNavigation` requires every `link` item to declare an `href`, but
 * the item's `onFollow` handler unconditionally calls
 * `event.preventDefault()` for this href so the browser never follows it
 * (see R1.12: "SHALL NOT change the V2 Route URL path"). `"#"` is a
 * conventional inert-anchor placeholder.
 */
const WIKI_LINK_HREF = "#";

/**
 * Stable identifier used to detect activation of the Wiki Link inside
 * the `onFollow` handler regardless of any future changes to
 * {@link WIKI_LINK_HREF}.
 */
const WIKI_LINK_ID = "wiki-link";

/**
 * Cloudscape's `SideNavigationProps.Link` type (as of `@amzn/awsui-
 * components-react@3.0.1860`) does not statically declare a `disabled`
 * boolean, though the requirements and the design explicitly call for
 * rendering the Wiki Link with `disabled: true`. This local type widens
 * the Cloudscape `Link` shape to accept the extra `disabled` field so we
 * can populate it as specified without a runtime cast.
 *
 * Inertness is guaranteed regardless by the `onFollow` handler below;
 * `disabled: true` is included here to satisfy the letter of the design
 * and to opt in to any current or future Cloudscape rendering that
 * respects the flag.
 */
type DisableableLink = SideNavigationProps.Link & { disabled?: boolean };

/**
 * Convert a router-facing pathname (which may or may not carry the `/v2`
 * prefix, depending on whether the caller is inside the App Shell's
 * `basename="/v2"` router or a plain `MemoryRouter`) into the single
 * {@link LeftNavPath} that identifies the active primary Left Nav item.
 *
 * The empty-string / root case returns {@link DEFAULT_LEFT_NAV_PATH}
 * (i.e. `/search`) so that the very first render of the App Shell -
 * before the `/` -> `/search` redirect in `AppShell.tsx` fires - still
 * highlights the "ASIN Search" item and never leaves the Left Nav in a
 * "no item active" state (R1.6 and R1.9).
 */
function derivePathFromLocation(pathname: string): LeftNavPath {
  const withoutV2Prefix = pathname.startsWith(V2_ROUTE_PREFIX)
    ? pathname.slice(V2_ROUTE_PREFIX.length) || "/"
    : pathname;
  return resolveLeftNavPath(withoutV2Prefix);
}

/**
 * Convert a {@link LeftNavPath} (router-relative, e.g. `/search`) into
 * its browser-visible V2 Route URL (e.g. `/v2/search`).
 */
function toBrowserHref(path: LeftNavPath): string {
  return `${V2_ROUTE_PREFIX}${path}`;
}

export const LeftNav: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const activePath = useMemo<LeftNavPath>(
    () => derivePathFromLocation(location.pathname),
    [location.pathname]
  );

  const activeHref = useMemo<string>(
    () => toBrowserHref(activePath),
    [activePath]
  );

  const items = useMemo<ReadonlyArray<SideNavigationProps.Item>>(() => {
    const primary: SideNavigationProps.Link[] = LEFT_NAV_ITEMS.map((item) => ({
      type: "link",
      text: item.label,
      href: toBrowserHref(item.path),
    }));

    const wikiLink: DisableableLink = {
      type: "link",
      text: WIKI_LINK_TEXT,
      href: WIKI_LINK_HREF,
      disabled: true,
    };

    return [...primary, { type: "divider" }, wikiLink];
  }, []);

  const handleFollow = useCallback(
    (event: CustomEvent<SideNavigationProps.FollowDetail>) => {
      const { href } = event.detail;

      // Activating the Wiki Link MUST NOT update the App
      // Shell navigation state, MUST NOT change the V2 Route URL path,
      // and MUST NOT modify the Global Search State. Preventing the
      // default anchor navigation and returning without dispatching any
      // router update satisfies all three constraints.
      if (href === WIKI_LINK_HREF) {
        event.preventDefault();
        return;
      }

      // R1.4: navigation between primary Left Nav items happens without a
      // full page reload. We suppress the browser's default anchor click
      // and hand the target to react-router's `navigate()`.
      event.preventDefault();

      // The Left Nav publishes browser-visible hrefs (`/v2/...`) but the
      // App Shell's router is scoped to `basename="/v2"`, which expects
      // paths relative to that basename. Strip the `/v2` prefix if
      // present, falling back to the default Left Nav path for a bare
      // `/v2` href (which should never occur for one of the four primary
      // items but is guarded defensively).
      const routerPath = href.startsWith(V2_ROUTE_PREFIX)
        ? href.slice(V2_ROUTE_PREFIX.length) || DEFAULT_LEFT_NAV_PATH
        : href;
      navigate(routerPath);
    },
    [navigate]
  );

  return (
    <SideNavigation
      header={{ text: HEADER_TEXT, href: toBrowserHref(DEFAULT_LEFT_NAV_PATH) }}
      items={items}
      activeHref={activeHref}
      onFollow={handleFollow}
    />
  );
};

// Export the disabled-Wiki-Link constants so unit tests can
// assert on the rendered inert affordance without duplicating the
// literals here.
export const LEFT_NAV_HEADER_TEXT = HEADER_TEXT;
export const LEFT_NAV_WIKI_LINK_TEXT = WIKI_LINK_TEXT;
export const LEFT_NAV_WIKI_LINK_HREF = WIKI_LINK_HREF;
export const LEFT_NAV_WIKI_LINK_ID = WIKI_LINK_ID;

export default LeftNav;
